import { PollyMessageService } from 'polly-commons';
import { inject, Injectable, signal } from '@angular/core';
import {
  AtlasExplorePayload,
  AtlasFilteredData,
  AtlasFilterPayload,
  AtlasPersistedFilters,
  AtlasTableColumnSchema,
  AtlasTableResource,
  ColumnFilters,
  NumericConditionalFilters,
} from '../@core/interfaces';
import { MixpanelPropertiesInterface } from '../../../polly-manage.interfaces';
import { CheckBoxOption, PaginatorData} from 'polly-qubit';
import { GTMEvents } from '../../../polly-manage-analytics.constants';
import { PollyManageAnalyticsService, PollyManageBackendService } from '../../../@core/services';
import { ATLAS_FILTERED_VALUES, ConditionalFilterOptions, DEFAULT_ATLAS_EXPLORE_PAYLOAD, DEFAULT_ATLAS_FILTER_PAYLOAD, TABLE_PAGINATOR_SIZE } from '../constants';
import { SortEvent } from 'primeng/api';
import { computed } from '@angular/core';
import {
  getAtlasExplorePayload,
  checkBetweenConditionValidity,
  findExistingFilterColumn,
  getTableMixpanelProperties,
  paginatorInitialData,
} from '../@core/utility';
import { catchError, defaultIfEmpty, map, Observable, of, tap } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class PollyAtlasFilterService {
  public readonly selectedTable = signal<AtlasTableResource | null>(null);
  public readonly filteredData = signal<AtlasFilteredData>(JSON.parse(JSON.stringify(ATLAS_FILTERED_VALUES)));
  public readonly existingExplorePayload = signal<AtlasExplorePayload>(JSON.parse(JSON.stringify(DEFAULT_ATLAS_EXPLORE_PAYLOAD)));
  public readonly allSelectedValueFilter = signal<{ [key: string]: CheckBoxOption[] }>({});
  public readonly allSelectedConditionFilter = signal<{ [key: string]: NumericConditionalFilters }>({});
  public readonly appliedFilterPayload = signal<AtlasFilterPayload>(JSON.parse(JSON.stringify(DEFAULT_ATLAS_FILTER_PAYLOAD)));
  public readonly isFilterOptionsNextUrlAvailable = signal<boolean>(false);
  public readonly filterPaginatorData = signal<PaginatorData>(paginatorInitialData(TABLE_PAGINATOR_SIZE));
  public readonly hasFiltersApplied = computed((): boolean => {
    const valueFilters = this.allSelectedValueFilter();
    const conditionFilters = this.allSelectedConditionFilter();
    const hasValueFilters = Object.values(valueFilters).some(filters => filters.length > 0);
    const hasConditionFilters = Object.keys(conditionFilters).length > 0;
    return hasValueFilters || hasConditionFilters;
  });
  private readonly manageBackendService = inject(PollyManageBackendService);
  private readonly manageAnalyticsService = inject(PollyManageAnalyticsService);
  private readonly messageService = inject(PollyMessageService);

  public applyColumnFilter(
    column: AtlasTableColumnSchema,
    isValueFilter: boolean,
    atlasId: string | undefined,
    atlasName: string | undefined,
    tableName: string | undefined,
    searchValue: string | null,
    options: CheckBoxOption[],
    selectedConditionFilterOption: ConditionalFilterOptions | null,
    selectedConditionFilterValue: NumericConditionalFilters,
  ): AtlasExplorePayload | null {
    if (this.selectedTable()) {
      this.existingExplorePayload.set(getAtlasExplorePayload(this.selectedTable()!, this.filteredData()));
    }
    const payload = structuredClone(this.existingExplorePayload());
    const existingFilterConditions = payload.data.attributes.column_filters.find((col) => col.column_name === column.column_name);
    if (existingFilterConditions) {
      delete existingFilterConditions.filter_conditions.contains;
    }
    const mixpanelProperties: MixpanelPropertiesInterface = getTableMixpanelProperties(atlasId, atlasName, tableName, column.column_name);
    const checkedColumnValues = this.allSelectedValueFilter()[column.column_name];
    const columnConditionSelection = this.allSelectedConditionFilter()[column.column_name];
    const { column_filters } = payload.data.attributes;
    const { column: existingFilterColumn, index: existingFilterColumnIndex } = findExistingFilterColumn(column_filters, column.column_name)
    payload.data.attributes.sort_by = column.column_name;
    if (isValueFilter) {
      if (!!checkedColumnValues && checkedColumnValues.length > 0) {
        const columnValueSelectionArray = checkedColumnValues.map((filter: CheckBoxOption) => filter.label);
        if (!!existingFilterColumn) {
          existingFilterColumn.filter_conditions.in = columnValueSelectionArray;
        } else {
          const columnFilter: ColumnFilters = { column_name: column.column_name, filter_conditions: { in: columnValueSelectionArray } };
          payload.data.attributes.column_filters = [columnFilter, ...column_filters];
        }
      } else {
        if (!!existingFilterColumn) {
          delete existingFilterColumn.filter_conditions.in;
          if (Object.keys(existingFilterColumn.filter_conditions).length === 0) {
            column_filters.splice(existingFilterColumnIndex, 1);
          }
        }
      }
      mixpanelProperties.Filter_Search_Value = searchValue || '';
      mixpanelProperties.Selected_Values = options.filter(option => option.checked).map(option => option.label);
      this.manageAnalyticsService.sendMixpanelEvent(GTMEvents.ATLAS_FILTER_VALUE, false, mixpanelProperties);
    } else {
      if (columnConditionSelection) {
        if (selectedConditionFilterOption === ConditionalFilterOptions.BETWEEN && selectedConditionFilterValue.between) {
          const isValidBetweenCondition = checkBetweenConditionValidity(selectedConditionFilterValue.between);
          if (!isValidBetweenCondition) {
            this.messageService.showToast('Error', 'Input both values for the between range filter correctly to work.', 'error');
            this.clearConditionalFiltersApplied(column_filters, column.column_name);
            return null;
          }
        }
      }
      if (!!existingFilterColumn) {
        const { in: inValue } = existingFilterColumn.filter_conditions;
        existingFilterColumn.filter_conditions = { ...columnConditionSelection, ...(inValue && { in: inValue })};
        if (Object.keys(existingFilterColumn.filter_conditions).length === 0) {
          column_filters.splice(existingFilterColumnIndex, 1);
        }
      } else {
        if (!columnConditionSelection) { return null; }
        const columnFilter: ColumnFilters = { column_name: column.column_name, filter_conditions: columnConditionSelection };
        payload.data.attributes.column_filters = [columnFilter, ...column_filters];
      }
    }

    this.existingExplorePayload.set(payload);
    return payload;
  }

  public onCheckboxChange(columnName: string, option: CheckBoxOption): void {
    this.allSelectedValueFilter.update(filters => {
      const updatedFilters = { ...filters };
      updatedFilters[columnName] ??= [];

      if (option.checked) {
        const existingOption = updatedFilters[columnName].find((selectedOption: CheckBoxOption) => selectedOption.value === option.value);
        if (!existingOption) {
          updatedFilters[columnName] = [...updatedFilters[columnName], option];
        }
      } else {
        updatedFilters[columnName] = updatedFilters[columnName].filter((selectedOption: CheckBoxOption) => selectedOption.value !== option.value);
      }

      return updatedFilters;
    });
  }

  public onConditionInput(
    col: AtlasTableColumnSchema,
    selectedConditionFilterOption: ConditionalFilterOptions | null,
    selectedConditionFilterValue: NumericConditionalFilters
  ): void {
    this.allSelectedConditionFilter.update(filters => {
      const updatedFilters = { ...filters };

      switch(selectedConditionFilterOption) {
        case ConditionalFilterOptions.GREATER_THAN: {
          updatedFilters[col.column_name] = { gt: selectedConditionFilterValue.gt };
          break;
        }
        case ConditionalFilterOptions.LESS_THAN: {
          updatedFilters[col.column_name] = { lt: selectedConditionFilterValue.lt };
          break;
        }
        case ConditionalFilterOptions.BETWEEN: {
          updatedFilters[col.column_name] = { between: selectedConditionFilterValue.between };
          break;
        }
        case ConditionalFilterOptions.EQUALS_TO: {
          updatedFilters[col.column_name] = { eq: selectedConditionFilterValue.eq };
          break;
        }
        default: break;
      }

      return updatedFilters;
    });
  }

  public resetFilters(): void {
    this.existingExplorePayload.set(JSON.parse(JSON.stringify(DEFAULT_ATLAS_EXPLORE_PAYLOAD)));
    this.appliedFilterPayload.set(JSON.parse(JSON.stringify(DEFAULT_ATLAS_FILTER_PAYLOAD)));
    this.allSelectedConditionFilter.set({});
    this.allSelectedValueFilter.set({});
  }

  public clearColumnFilters(column: AtlasTableColumnSchema, isValueFilter: boolean): void {
    if (isValueFilter) {
      this.allSelectedValueFilter.update(filters => {
        const updatedFilters = { ...filters };
        delete updatedFilters[column.column_name];
        return updatedFilters;
      });
    } else {
      this.allSelectedConditionFilter.update(filters => {
        const updatedFilters = { ...filters };
        delete updatedFilters[column.column_name];
        return updatedFilters;
      });
    }
  }

  public clearConditionalFiltersApplied(columnFilters: ColumnFilters[], columnName: string): void {
    const filteredColumnIndex = columnFilters.findIndex((col) => col.column_name === columnName);
    if (filteredColumnIndex !== -1 && columnFilters[filteredColumnIndex] && columnFilters[filteredColumnIndex].filter_conditions) {
      const { in: inValue } = columnFilters[filteredColumnIndex].filter_conditions;
      columnFilters[filteredColumnIndex].filter_conditions = inValue ? { in: inValue } : {};
    }
  }

  public sortTable(event: SortEvent): void {
    this.filteredData.update(data => ({
      ...data,
      filterStatus: {
        ...data.filterStatus,
        sort_by: event.field || '',
        sort_order: event.order === 1 ? 'asc' : 'desc'
      }
    }));
  }

  public persistFilters(atlasId: string, tableName: string): void {
    const state: AtlasPersistedFilters = {
      filteredData: this.filteredData(),
      valueFilters: this.allSelectedValueFilter(),
      conditionFilters: this.allSelectedConditionFilter()
    };
    const key = this.getStorageKey(atlasId, tableName);
    this.manageBackendService.indexedDB.setDB<AtlasPersistedFilters>(key, state).subscribe();
  }

  public restoreFilters(atlasId: string, tableName: string): Observable<void> {
    const key = this.getStorageKey(atlasId, tableName);
    return this.manageBackendService.indexedDB.getDB<AtlasPersistedFilters>(key)
      .pipe(
        defaultIfEmpty(null),
        tap((state: AtlasPersistedFilters | null): void => {
          if (!state) { return; }
          this.filteredData.set(state.filteredData);
          this.allSelectedValueFilter.set(state.valueFilters);
          this.allSelectedConditionFilter.set(state.conditionFilters);
          const selectedTable = this.selectedTable();
          if (selectedTable) {
            const payload = getAtlasExplorePayload(selectedTable, this.filteredData());
            this.existingExplorePayload.set(payload);
          }
        }),
        map(() => void 0),
        catchError(() => of(void 0))
      );
  }

  private getStorageKey(atlasId: string, tableName: string): string {
    return `atlas-${atlasId}-${tableName}-filters`;
  }
}
